
import { supabase } from '@/integrations/supabase/client';

import { toast } from 'sonner';
import { trackValidationError } from './upload-error-tracker';

export const validateFile = async (file: File, isPremiumUser: boolean, allowMatureContent: boolean = false): Promise<{ file: File, isMature: boolean }> => {
  console.log('Starting file validation for:', {
    name: file.name,
    type: file.type,
    size: file.size,
    isPremiumUser,
    allowMatureContent
  });

  if (!file) {
    console.error('No file provided');
    const error = new Error('No file provided');
    trackValidationError('No file provided', undefined, error);
    throw error;
  }

  if (!(file instanceof File)) {
    console.error('Invalid file object:', file);
    const error = new Error('Invalid file format');
    trackValidationError('Invalid file object - not a File instance', undefined, error);
    throw error;
  }

  if (file.size === 0) {
    console.error('File is empty');
    const error = new Error('File cannot be empty');
    trackValidationError('File has zero bytes', file, error);
    throw error;
  }

  // Standard MIME types that browsers actually use
  const validImageTypes = ['image/jpeg', 'image/png', 'image/heic'];
  const validPremiumTypes = [...validImageTypes, 'image/gif', 'video/mp4'];
  const validTypes = isPremiumUser ? validPremiumTypes : validImageTypes;

  // Log the actual file type for debugging
  console.log('File type check:', {
    actualType: file.type,
    validTypes,
    isValid: validTypes.includes(file.type)
  });

  if (!validTypes.includes(file.type)) {
    console.error('Invalid file type:', file.type);
    const errorMsg = `Invalid file type. Supported formats: ${
      isPremiumUser ? 'JPG, PNG, GIF, MP4' : 'JPG, PNG, HEIC'
    }`;
    const error = new Error(errorMsg);
    trackValidationError(`Invalid MIME type: ${file.type}`, file, error);
    throw error;
  }

  const MAX_SIZE = 50 * 1024 * 1024; // 50MB input (will be compressed)

  if (file.size > MAX_SIZE) {
    console.error('File too large:', file.size);
    const sizeMB = (file.size / 1024 / 1024).toFixed(1);
    const error = new Error(`File is too large (${sizeMB}MB). Maximum size is 50MB. Please choose a smaller file.`);
    trackValidationError(`File too large: ${sizeMB}MB`, file, error);
    throw error;
  }
  
  // Log file size for monitoring
  const sizeMB = (file.size / 1024 / 1024).toFixed(2);
  console.log('File size check passed:', {
    sizeMB: sizeMB + 'MB',
    willCompress: file.size > 2 * 1024 * 1024 && file.type.startsWith('image/')
  });

  // Verify the file is readable by attempting to read a small chunk
  try {
    const chunk = await file.slice(0, 1024).arrayBuffer();
    if (!chunk || chunk.byteLength === 0) {
      const error = new Error('File appears to be corrupted or unreadable');
      trackValidationError('File chunk read returned empty buffer', file, error);
      throw error;
    }
  } catch (error) {
    console.error('Error reading file:', error);
    const readError = new Error('File appears to be corrupted or unreadable');
    trackValidationError(
      `File read failed: ${error instanceof Error ? error.message : 'Unknown'}`,
      file,
      error instanceof Error ? error : readError
    );
    throw readError;
  }

  // No client-side moderation - all content will be reviewed in background
  // Images will be moderated automatically after upload
  let isMature = false; // Default to false, will be determined by background processing
  
  console.log('File validation completed - content moderation will be handled in background');

  console.log('File validation passed:', {
    name: file.name,
    type: file.type,
    size: file.size,
    isMature
  });

  return { file, isMature };
};

export const sanitizeFileName = (name: string): string => {
  // Remove non-ASCII characters but preserve dots for extension
  const sanitized = name
    .replace(/[^a-zA-Z0-9\-_.]/g, '_') // Replace special chars with underscores
    .replace(/_{2,}/g, '_') // Remove consecutive underscores
    .replace(/^\.+/g, '') // Remove leading dots
    .replace(/\.+$/, ''); // Preserve last dot for extension
  
  // Extract extension safely
  const lastDotIndex = sanitized.lastIndexOf('.');
  if (lastDotIndex === -1) return sanitized;
  
  const nameWithoutExt = sanitized.slice(0, lastDotIndex);
  const extension = sanitized.slice(lastDotIndex + 1);
  
  return `${nameWithoutExt}.${extension}`;
};
